/**
 * FibiWhatsAppBoton.jsx · v101.200.0 · Botón «WhatsApp» en la barra de la OT: abre la conversación con el cliente en
 * una ventana flotante sobre la misma pantalla (sin ir a Conversaciones). Los agendadores pueden tomarla y chatear;
 * los demás roles la ven en solo lectura. Si no hay conversación, permite que Fibi contacte al cliente.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Bot, Loader2, MessageCircle, X } from "lucide-react";
import { Chat } from "../pages/FibiConversaciones";

const ROLES_AGENTE = ["super_admin", "admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"];
const ESTADO = { en_espera: "En espera", humano: "Con agente", bot: "Con Fibi", cerrada: "Cerrada" };
const hora = (v) => (v ? new Date(v).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function SoloLectura({ c }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 14, background: "#F1F5F9", borderRadius: 14, maxHeight: "60vh", overflowY: "auto" }}>
      {c.mensajes.map((m) => (
        <div key={m.id} style={{ alignSelf: m.direccion === "nota" ? "center" : m.autor === "cliente" ? "flex-start" : "flex-end", maxWidth: "80%",
          fontSize: m.direccion === "nota" ? 11 : 13, color: m.direccion === "nota" ? "#64748B" : "#0F172A",
          background: m.direccion === "nota" ? "transparent" : m.autor === "cliente" ? "#FFFFFF" : m.autor === "fibi" ? "#DCFCE7" : "#DBEAFE",
          padding: m.direccion === "nota" ? 0 : "8px 11px", borderRadius: 12, whiteSpace: "pre-wrap" }}>
          <div style={{ fontSize: 10.5, color: "#64748B" }}>{m.autor === "cliente" ? "Cliente" : m.autor === "fibi" ? "Fibi" : m.autor === "agente" ? (m.autor_nombre || "Agente") : ""} {hora(m.created_at)}</div>
          {/^\[sticker:/.test(m.texto || "") ? "🖼️ sticker" : m.texto}
        </div>
      ))}
    </div>
  );
}

export default function FibiWhatsAppBoton({ odooTaskId, taskNumber }) {
  const { user } = useAuth() || {};
  const agente = ROLES_AGENTE.includes(String(user?.role || "").toLowerCase());
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!odooTaskId) return;
    try {
      const { data } = await api.get(`/v1/fibi/ot/${odooTaskId}`);
      setItems(data.items || []);
      setSel((s) => s || (data.items?.[0]?.id ?? null));
    } catch (e) { setItems(e?.response?.status === 403 ? null : []); }
  }, [odooTaskId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(load, agente ? 15000 : 8000);
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => { clearInterval(t); window.removeEventListener("keydown", esc); };
  }, [open, load, agente]);
  if (items === null) return null;
  const espera = items.filter((c) => c.estado === "en_espera").length;
  const abiertas = items.filter((c) => c.estado !== "cerrada").length;
  const contactar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/v1/fibi/iniciar", { odoo_task_id: odooTaskId }, { noProcess: true });
      toast.success("Fibi saludó al cliente por WhatsApp");
      setSel(data.conversacion); await load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo contactar");
    } finally { setBusy(false); }
  };
  const actual = items.find((c) => c.id === sel) || items[0];
  return (
    <>
      <button
        onClick={() => { setOpen(true); load(); }}
        data-testid="btn-ot-whatsapp"
        title="Ver la conversación de WhatsApp con el cliente"
        className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 text-white font-semibold relative"
        style={{ background: "#16A34A" }}
      >
        <MessageCircle size={13} /> WhatsApp
        {items.length > 0 && (
          <span style={{ marginLeft: 2, background: espera ? "#F59E0B" : "rgba(255,255,255,.25)", borderRadius: 999, padding: "0 6px", fontSize: 10.5 }}>
            {espera ? `${espera} en espera` : abiertas || items.length}
          </span>
        )}
      </button>
      {open && (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "min(900px, 100%)", maxHeight: "92vh", overflowY: "auto", background: "#ECF0F5", borderRadius: 22, boxShadow: "0 24px 60px rgba(15,23,42,.35)", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}
            data-testid="ot-whatsapp-modal">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 30, height: 30, borderRadius: 999, background: "#16A34A", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><MessageCircle size={16} color="#fff" /></span>
              <div style={{ flex: 1, fontWeight: 800, color: "#0A3747", fontSize: 15 }}>WhatsApp con el cliente · OT {taskNumber || odooTaskId}</div>
              {agente && <button onClick={contactar} disabled={busy} style={{ border: 0, borderRadius: 10, padding: "7px 11px", background: "#0A3747", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "inline-flex", gap: 5, alignItems: "center" }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Bot size={13} />} Contactar con Fibi</button>}
              <button onClick={() => setOpen(false)} title="Cerrar" style={{ border: 0, background: "transparent", cursor: "pointer" }}><X size={20} color="#0A3747" /></button>
            </div>
            {items.length > 1 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {items.map((c) => (
                  <button key={c.id} onClick={() => setSel(c.id)} style={{ border: 0, borderRadius: 999, padding: "5px 10px", cursor: "pointer", fontSize: 12,
                    background: actual?.id === c.id ? "#0A3747" : "#E4EAF0", color: actual?.id === c.id ? "#FFF" : "#0A3747" }}>
                    #{c.id} · {hora(c.created_at)} · {ESTADO[c.estado] || c.estado}
                  </button>
                ))}
              </div>
            )}
            {items.length === 0 && (
              <div style={{ padding: 30, textAlign: "center", color: "#475569", fontSize: 13 }}>
                Esta OT todavía no tiene conversaciones de WhatsApp con el cliente.
                {agente ? " Pulse «Contactar con Fibi» para que lo salude y le ofrezca horarios." : ""}
              </div>
            )}
            {actual && (agente ? <Chat key={actual.id} cid={String(actual.id)} onChanged={load} /> : <SoloLectura c={actual} />)}
          </div>
        </div>
      )}
    </>
  );
}
